import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, Plus } from "lucide-react";
import { useState } from "react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  Input,
  Select,
  StatusBadge,
  cn,
  type Column,
} from "../../shared/ui";
import type {
  PageResponse,
  Priority,
  ProjectModuleView,
  WorkItemStatus,
  WorkItemSummary,
  WorkItemType,
} from "../../shared/types/api";
import {
  ArchiveGroupField,
  reopenArchived,
  reopenColumn,
  useArchiveGroups,
  useArchivedItems,
} from "./archiveGroups";
import { BulkMoveBar } from "./BulkMoveBar";
import { WorkItemFormDrawer } from "./WorkItemFormDrawer";
import { WorkItemStatusBadge, WorkItemTypeBadge } from "./WorkItemTypeBadge";
import {
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  PRIORITY_TONE,
  REVIEW_LABELS,
  REVIEW_TONE,
  SEVERITY_LABELS,
  SEVERITY_TONE,
  STATUS_LABELS,
  STATUS_ORDER,
  TYPE_LABELS,
  formatHours,
  formatSlippage,
  needsReview,
  needsRetest,
} from "./workItemVocabulary";

const TYPES: WorkItemType[] = [
  "EPIC",
  "USER_STORY",
  "TECHNICAL_TASK",
  "BUG",
  "SPIKE",
  "SUBTASK",
  "ANALYSIS",
  "DEMO_PREPARATION",
  "MIGRATION",
  "SERVER_WORK",
];

/**
 * The work under a project (§15).
 *
 * <p>A flat, filterable list rather than a tree. A tree is the right shape for one epic and its
 * stories, and it is on the item's own page; a project with four hundred items needs filtering
 * and searching far more than it needs indentation, and the parent key on each row keeps the
 * hierarchy legible without it.
 */
/** Ten rows a page to begin with, server-side: the browser never holds more of a project than
 * it shows. The reader can ask for more, and that choice rides in the URL with the filters. */
const DEFAULT_PAGE_SIZE = "10";

/* The parameters this screen owns, and what each means when absent. Defined outside the
   component so its identity is stable — useUrlFilters memoises on it. */
const WORK_FILTERS = {
  q: "", type: "", status: "", priority: "", moduleId: "", page: "0", size: DEFAULT_PAGE_SIZE,
  dueFrom: "", dueTo: "", group: "",
};

export function ProjectWorkTab({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();

  /* In the URL, not in this component: see useUrlFilters. Opening an item and coming back used
     to land on an unfiltered list, because the filters unmounted with the page. */
  const [filters, setFilters] = useUrlFilters(WORK_FILTERS);
  const status = filters.status as WorkItemStatus | "";
  const type = filters.type as WorkItemType | "";
  const priority = filters.priority as Priority | "";
  const { q: query, moduleId, dueFrom, dueTo, group } = filters;
  /* Reading a group is a different question from filtering the live list, so it replaces the
     table rather than narrowing it: the rows in a group are precisely the ones every other
     filter here is built to exclude. */
  const viewingGroup = group !== "";
  const page = Number(filters.page) || 0;
  const pageSize = Number(filters.size) || Number(DEFAULT_PAGE_SIZE);

  const [creating, setCreating] = useState(false);
  const [reopening, setReopening] = useState<string | null>(null);
  /* Cleared whenever the rows underneath change. A selection that survived a filter would act on
     items the person can no longer see, which is the one thing a batch must never do. */
  const [selected, setSelected] = useState<string[]>([]);
  const edit = can("WORK_ITEM_EDIT");

  /* Any filter change goes back to the first page. Staying on page three of a narrower result
     shows an empty table and reads as "nothing matches", which is the wrong answer. */
  const refilter = (patch: Partial<typeof WORK_FILTERS>) => {
    setFilters({ ...patch, page: "0" });
    setSelected([]);
  };

  /* What this project is built from. Drives both the Module column and its filter — a product
     that is one thing gets neither, rather than a column of dashes. */
  const modules = useQuery({
    queryKey: ["project-modules", projectId],
    queryFn: () => api.get<ProjectModuleView[]>(`/api/v1/projects/${projectId}/modules`),
  });
  const hasModules = (modules.data ?? []).length > 0;


  const items = useQuery({
    queryKey: [
      "work-items", projectId, status, type, priority, query, moduleId, dueFrom, dueTo,
      page, pageSize,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        projectId,
        page: String(page),
        size: String(pageSize),
      });
      if (moduleId) {
        params.set("moduleId", moduleId);
      }
      if (status) {
        params.set("status", status);
      }
      if (type) {
        params.set("type", type);
      }
      if (priority) {
        params.set("priority", priority);
      }
      /* Either end stands on its own: "due by Friday" and "due from Monday" are both questions
         somebody asks, and neither needs the other half filled in first. */
      if (dueFrom) {
        params.set("dueFrom", dueFrom);
      }
      if (dueTo) {
        params.set("dueTo", dueTo);
      }
      if (query.trim()) {
        params.set("q", query.trim());
      }
      return api.get<PageResponse<WorkItemSummary>>(`/api/v1/work-items?${params}`);
    },
  });
  const groups = useArchiveGroups(projectId);
  const archived = useArchivedItems(group);

  const reopen = (itemId: string) => {
    setReopening(itemId);
    void (async () => {
      try {
        await reopenArchived(itemId);
        /* Both: the item leaves the group, and the count beside the group's name moves with it. */
        await Promise.all([archived.refetch(), groups.refetch(), items.refetch()]);
      } finally {
        setReopening(null);
      }
    })();
  };

  /*
    Which part of the product this belongs to. Offered only where the project actually has
    modules — a column of dashes on a product that is one thing teaches people to stop reading
    the columns.
  */
  const moduleColumn: Column<WorkItemSummary> = {
    key: "moduleName",
    header: "Module",
    render: (item) =>
      item.moduleName ? (
        <span className="text-sm text-ink-800">{item.moduleName}</span>
      ) : (
        <span className="text-ink-300">—</span>
      ),
  };

  const rows = viewingGroup ? (archived.data ?? []) : (items.data?.content ?? []);
  const allShownSelected = rows.length > 0 && rows.every((row) => selected.includes(row.id));

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );

  const columns: Column<WorkItemSummary>[] = [
    /* No ticking while a group is open: every batch action acts on live work, and the rows here
       have already left it. */
    ...(edit && !viewingGroup
      ? [
          {
            key: "select",
            header: (
              <input
                type="checkbox"
                aria-label="Select every item on this page"
                checked={allShownSelected}
                onChange={() =>
                  setSelected(allShownSelected ? [] : rows.map((row) => row.id))
                }
                className="h-4 w-4 rounded border-ink-300"
              />
            ),
            className: "w-10",
            render: (item: WorkItemSummary) => (
              <input
                type="checkbox"
                aria-label={`Select ${item.itemKey}`}
                checked={selected.includes(item.id)}
                /* The row itself opens the item, so the tick must not also do that. */
                onClick={(event) => event.stopPropagation()}
                onChange={() => toggle(item.id)}
                className="h-4 w-4 rounded border-ink-300"
              />
            ),
          },
        ]
      : []),
    {
      key: "itemKey",
      header: "Item",
      render: (item) => (
        <span className="flex items-center gap-2">
          <WorkItemTypeBadge type={item.workItemType} />
          <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>
        </span>
      ),
    },
    {
      key: "title",
      header: "Title",
      render: (item) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-ink-900">{item.title}</span>
          {/* A blocked item is the one thing on this row worth interrupting a scan for. */}
          {item.openBlockerCount > 0 && (
            <span
              className="flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700"
              title={`${item.openBlockerCount} open blocker${item.openBlockerCount === 1 ? "" : "s"}`}
            >
              <AlertOctagon className="h-3 w-3" />
              {item.openBlockerCount}
            </span>
          )}
          {item.parentKey && (
            <span className="font-mono text-xs text-ink-400">in {item.parentKey}</span>
          )}
        </span>
      ),
    },
    {
      key: "review",
      header: "Review",
      /* Two different things can be outstanding here, and the later one wins the badge: a task
         whose review is approved is not waiting for a reviewer, it is waiting for a tester. */
      render: (item: WorkItemSummary) =>
        needsRetest(item) ? (
          <StatusBadge tone="amber">Retest</StatusBadge>
        ) : needsReview(item) ? (
          <StatusBadge tone={REVIEW_TONE[item.reviewState]}>
            {REVIEW_LABELS[item.reviewState]}
          </StatusBadge>
        ) : (
          <span className="text-ink-300">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => <WorkItemStatusBadge status={item.status} />,
    },
    {
      /*
        How bad a fault is, in a column of its own — the same shape the bug screen uses.
        Both facts used to share the Priority column, which read as two competing labels on
        every bug. Keeping them apart is not cosmetic: they answer different questions, and
        collapsing them into one is what made the Priority filter look broken, since narrowing
        to Critical returned rows every one of which was labelled "Blocker".
      */
      key: "severity",
      header: "Severity",
      render: (item) =>
        item.severity ? (
          <StatusBadge tone={SEVERITY_TONE[item.severity]}>
            {SEVERITY_LABELS[item.severity]}
          </StatusBadge>
        ) : (
          /* Blank for everything that is not a bug, which is most of this list. */
          <span className="text-ink-400">—</span>
        ),
    },
    {
      key: "priority",
      header: "Priority",
      /* When we will get to it, and nothing else. */
      render: (item) => (
        <StatusBadge tone={PRIORITY_TONE[item.priority]}>
          {PRIORITY_LABELS[item.priority]}
        </StatusBadge>
      ),
    },
    {
      /* §10: which release is carrying this. Blank is the ordinary state for backlog work. */
      key: "releaseVersion",
      header: "Release",
      render: (item) =>
        item.releaseVersion ? (
          <span className="text-sm text-ink-800" title={item.releaseKey ?? undefined}>
            {item.releaseVersion}
          </span>
        ) : (
          <span className="text-ink-300">—</span>
        ),
    },
    {
      key: "assigneeName",
      header: "Assignee",
      render: (item) =>
        item.assigneeName ?? <span className="text-ink-400">Unassigned</span>,
    },
    {
      key: "currentEstimateHours",
      header: "Estimate",
      className: "text-right tnum",
      render: (item) => formatHours(item.currentEstimateHours),
    },
    {
      key: "committedDueDate",
      header: "Due",
      className: "tnum",
      render: (item) =>
        item.committedDueDate ? (
          <span
            className={cn(
              item.scheduleVarianceDays > 0 ? "font-medium text-red-600" : "text-ink-900",
            )}
            title={formatSlippage(item.scheduleVarianceDays)}
          >
            {item.committedDueDate}
          </span>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    },
  ];

  /* Inserted before Release, so the two "which part of the picture" columns sit together. */
  const withModule = hasModules
    ? [...columns.slice(0, 3), moduleColumn, ...columns.slice(3)]
    : columns;
  const shownColumns = viewingGroup && edit
    ? [...withModule, reopenColumn(reopen, reopening)]
    : withModule;

  return (
    <>
      <Card className="mb-4">
        <div className="flex items-end justify-between gap-3 p-4">
          <div className="flex flex-1 flex-wrap items-end gap-3 lg:flex-nowrap">
          <div className="min-w-36 flex-1">
            <Field label="Search">
              <Input
                placeholder="Title or key"
                value={query}
                onChange={(event) => refilter({ q: event.target.value })}
              />
            </Field>
          </div>
          {hasModules && (
            <div className="w-32 shrink-0">
              <Field label="Module">
                <Select
                  value={moduleId}
                  onChange={(event) => refilter({ moduleId: event.target.value })}
                >
                  <option value="">All modules</option>
                  {(modules.data ?? []).map((row) => (
                    <option key={row.module.id} value={row.module.id}>
                      {row.module.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          <div className="w-32 shrink-0">
            <Field label="Type">
              <Select
                value={type}
                onChange={(event) => refilter({ type: event.target.value })}
              >
                <option value="">All types</option>
                {TYPES.map((value) => (
                  <option key={value} value={value}>
                    {TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-32 shrink-0">
            <Field label="Status">
              <Select
                value={status}
                onChange={(event) => refilter({ status: event.target.value })}
              >
                <option value="">All statuses</option>
                {STATUS_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {/* Narrowed on the server like the others. Filtering the page in the browser would
              answer "the critical ones among these ten", which is not the question anybody
              opening this asks. */}
          <div className="w-32 shrink-0">
            <Field label="Priority">
              <Select
                value={priority}
                onChange={(event) => refilter({ priority: event.target.value })}
              >
                <option value="">All priorities</option>
                {PRIORITY_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {/* One span under one label. Two separate fields cost a second label column and a
              second gap, which is what pushed this row onto three lines. Either end may still
              be left empty: "due by Friday" and "due from Monday" are both whole questions. */}
          <div className="shrink-0">
            <Field label="Due between">
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  aria-label="Due from"
                  className="w-[8.5rem]"
                  value={dueFrom}
                  max={dueTo || undefined}
                  onChange={(event) => refilter({ dueFrom: event.target.value })}
                />
                <span className="text-sm text-ink-400">–</span>
                <Input
                  type="date"
                  aria-label="Due to"
                  className="w-[8.5rem]"
                  value={dueTo}
                  /* The browser refuses a backwards span before the server has to. */
                  min={dueFrom || undefined}
                  onChange={(event) => refilter({ dueTo: event.target.value })}
                />
              </div>
            </Field>
          </div>
          <ArchiveGroupField
            groups={groups.data ?? []}
            value={group}
            /* Its own reset rather than refilter's: the page number belongs to the live list,
               and a group is read whole. */
            onChange={(next) => {
              setFilters({ group: next, page: "0" });
              setSelected([]);
            }}
          />
          </div>
          {can("WORK_ITEM_CREATE") && (
            <Button
              className="shrink-0"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setCreating(true)}
            >
              New item
            </Button>
          )}
        </div>
      </Card>

      {items.isError ? (
        <ErrorState error={items.error} onRetry={() => void items.refetch()} />
      ) : (
        <>
        {!viewingGroup && (
        <BulkMoveBar
          items={rows.filter((row) => selected.includes(row.id))}
          onClear={() => setSelected([])}
          onDone={(allMoved) => {
            if (allMoved) {
              setSelected([]);
            }
            void items.refetch();
          }}
        />
        )}
        <DataTable
          columns={shownColumns}
          rows={rows}
          rowKey={(item) => item.id}
          loading={viewingGroup ? archived.isPending : items.isPending}
          /* A group is read whole, so it carries no paging controls — omitting onPageChange is
             what hides them. */
          page={viewingGroup ? 0 : page}
          pageSize={viewingGroup ? rows.length || 1 : pageSize}
          totalElements={viewingGroup ? rows.length : (items.data?.totalElements ?? 0)}
          onPageChange={viewingGroup ? undefined : (next) => {
            setFilters({ page: String(next) });
            setSelected([]);
          }}
          onPageSizeChange={viewingGroup ? undefined : (next) => {
            setFilters({ size: String(next), page: "0" });
            setSelected([]);
          }}
          onRowClick={(item) => navigate(`/work-items/${item.itemKey}`)}
          emptyTitle={
            viewingGroup
              ? "This group is empty"
              : query || status || type
                ? "Nothing matches those filters"
                : "No work yet"
          }
          emptyMessage={
            viewingGroup
              ? "Everything that was in it has been reopened."
              : query || status || type
                ? "Try a broader filter — the work may exist under another type or status."
                : "Break the project down into epics, stories, tasks, bugs and spikes. Nothing has to follow one shape."
          }
          emptyAction={
            can("WORK_ITEM_CREATE") &&
            !viewingGroup &&
            !query &&
            !status &&
            !type && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                Create the first item
              </Button>
            )
          }
        />
        </>
      )}

      <WorkItemFormDrawer
        projectId={projectId}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => void items.refetch()}
      />
    </>
  );
}
